import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Search, BookOpen, Clock, User } from 'lucide-react';
import './KnowledgeBasePage.css';

const STATUS_LABELS = {
    draft:     'Черновик',
    review:    'На проверке',
    revision:  'На доработке',
    published: 'Опубликован',
};

function formatDate(dt) {
    if (!dt) return '';
    const raw = String(dt).trim();
    const withZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(raw)
        ? raw
        : `${raw.replace(' ', 'T')}Z`;
    return new Date(withZone).toLocaleDateString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

// Вырезаем HTML-теги для предпросмотра
function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
}

const KnowledgeBasePage = () => {
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');

    const { data: articles = [], isLoading, isFetching } = useQuery(
        ['knowledgeSearch', query],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge/search`, {
                params: { q: query },
                withCredentials: true
            });
            return res.data;
        },
        { keepPreviousData: true }
    );

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        setQuery(searchInput.trim());
    }, [searchInput]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch(e);
    };

    const openArticle = (article) => {
        const params = query ? `?highlight=${encodeURIComponent(query)}` : '';
        navigate(`/knowledge/${article.id}${params}`);
    };

    return (
        <div className="knowledge-base-page">
            <div className="knowledge-header">
                <div className="knowledge-header-icon">
                    <BookOpen size={32} />
                </div>
                <h1>База знаний</h1>
                <p className="knowledge-subtitle">Поиск по статьям</p>
            </div>

            <div className="knowledge-search-bar">
                <div className="knowledge-search-input-wrap">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Поиск по названию и содержимому статей..."
                        className="knowledge-search-input"
                        autoFocus
                    />
                    {searchInput && (
                        <button
                            className="knowledge-search-clear"
                            onClick={() => { setSearchInput(''); setQuery(''); }}
                            type="button"
                        >×</button>
                    )}
                </div>
                <button className="knowledge-search-btn" onClick={handleSearch} type="button">
                    Найти
                </button>
            </div>

            {query && (
                <div className="knowledge-search-meta">
                    {isFetching ? 'Поиск...' : `Найдено: ${articles.length}`}
                    {articles.length > 0 && ` результатов по запросу «${query}»`}
                </div>
            )}

            {isLoading ? (
                <div className="knowledge-loading">
                    <div className="spinner" />
                </div>
            ) : articles.length === 0 ? (
                <div className="knowledge-empty">
                    {query
                        ? `По запросу «${query}» статьи не найдены`
                        : 'Статьи пока не опубликованы'}
                </div>
            ) : (
                <div className="knowledge-articles-list">
                    {articles.map(article => (
                        <div
                            key={article.id}
                            className="knowledge-article-card"
                            onClick={() => openArticle(article)}
                        >
                            <div className="knowledge-article-card-content">
                                <h2 className="knowledge-article-title">
                                    {article.title}
                                </h2>
                                {article.excerpt && (
                                    <p className="knowledge-article-excerpt">
                                        {stripHtml(article.excerpt).slice(0, 200)}
                                        {stripHtml(article.excerpt).length > 200 ? '...' : ''}
                                    </p>
                                )}
                            </div>
                            <div className="knowledge-article-meta">
                                <span className="knowledge-meta-item">
                                    <User size={14} />
                                    {article.author_name}
                                </span>
                                <span className="knowledge-meta-item">
                                    <Clock size={14} />
                                    {formatDate(article.updated_at)}
                                </span>
                                {article.match_in === 'content' && query && (
                                    <span className="knowledge-match-badge">в тексте</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default KnowledgeBasePage;
